import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Download, Analytics } from '@mui/icons-material';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';
import { fetchReportsData } from '../store/slices/reportsSlice';

const Reports = () => {
  const dispatch = useDispatch();
  const { reportsData, loading, error } = useSelector((state) => state.reports);
  
  const [filters, setFilters] = useState({
    dateRange: 'last_30_days',
    startDate: null,
    endDate: null,
    reportType: 'leads'
  });

  useEffect(() => {
    dispatch(fetchReportsData(filters));
  }, [dispatch, filters]);

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleDateChange = (field) => (date) => {
    setFilters(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const exportReport = () => {
    // TODO: Implement report export functionality
    console.log('Exporting report with filters:', filters);
  };

  if (loading) {
    return <LoadingSpinner message="Loading reports..." />;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Reports & Analytics
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Generate and view detailed reports about your CRM data.
      </Typography>

      {error && (
        <ErrorAlert 
          error={error} 
          onClose={() => dispatch({ type: 'reports/clearError' })} 
        />
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Report Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={filters.reportType}
                  label="Report Type"
                  onChange={handleFilterChange('reportType')}
                >
                  <MenuItem value="leads">Leads Report</MenuItem>
                  <MenuItem value="sales">Sales Report</MenuItem>
                  <MenuItem value="user_performance">User Performance</MenuItem>
                  <MenuItem value="conversion">Conversion Report</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={filters.dateRange}
                  label="Date Range"
                  onChange={handleFilterChange('dateRange')}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                  <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                  <MenuItem value="last_90_days">Last 90 Days</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {filters.dateRange === 'custom' && (
              <>
                <Grid item xs={12} sm={6} md={2}>
                  <DatePicker
                    label="Start Date"
                    value={filters.startDate}
                    onChange={handleDateChange('startDate')}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <DatePicker
                    label="End Date"
                    value={filters.endDate}
                    onChange={handleDateChange('endDate')}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={exportReport}
                fullWidth
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Report Content */}
      {reportsData ? (
        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Leads
                </Typography>
                <Typography variant="h4">
                  {reportsData.totalLeads || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Converted Leads
                </Typography>
                <Typography variant="h4" color="success.main">
                  {reportsData.convertedLeads || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Conversion Rate
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {reportsData.conversionRate || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Revenue Generated
                </Typography>
                <Typography variant="h4" color="success.main">
                  ${reportsData.revenue || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Detailed Report */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Detailed {filters.reportType.replace('_', ' ').toUpperCase()} Report
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {reportsData.details ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Report data would be displayed here based on the selected filters.
                    </Typography>
                    {/* TODO: Add actual report visualization components */}
                  </Box>
                ) : (
                  <Alert severity="info">
                    No data available for the selected filters. Try adjusting your date range or report type.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Card>
          <CardContent>
            <Alert severity="info" icon={<Analytics />}>
              Select your report filters above to generate a report.
            </Alert>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Reports;